import React, { useEffect, useMemo, useRef, useState } from 'react';
import { DayPicker, DateRange } from 'react-day-picker';
import { id as localeId } from 'date-fns/locale';
import { format, parse, isValid, startOfMonth, startOfToday, subDays } from 'date-fns';
import { Calendar as CalendarIcon, X } from 'lucide-react';
import 'react-day-picker/dist/style.css';

const dayPickerClassNames = {
  months: 'flex flex-col sm:flex-row gap-4',
  month: 'space-y-3',
  caption: 'flex justify-start pt-1 pl-1 relative items-center h-7',
  caption_label: 'text-sm font-medium text-white capitalize',
  nav: 'absolute right-1 top-0 flex items-center gap-1',
  nav_button:
    'h-7 w-7 inline-flex items-center justify-center rounded-lg text-white/60 hover:text-cyan-400 hover:bg-white/10 transition-colors',
  nav_button_previous: '',
  nav_button_next: '',
  table: 'w-full border-collapse',
  head_row: 'flex',
  head_cell: 'w-9 text-[11px] font-normal text-white/40 capitalize',
  row: 'flex w-full mt-1',
  cell: 'relative p-0 text-center',
  day: 'h-9 w-9 inline-flex items-center justify-center rounded-lg text-sm text-white/80 hover:bg-white/10 hover:text-white transition-colors',
  day_today: 'ring-1 ring-cyan-400/50 text-cyan-300',
  day_selected: '!bg-cyan-400 !text-[#050914] font-medium hover:!bg-cyan-300',
  day_range_middle: '!bg-cyan-400/15 !text-cyan-100 !rounded-none',
  day_range_start: '!bg-cyan-400 !text-[#050914] font-medium',
  day_range_end: '!bg-cyan-400 !text-[#050914] font-medium',
  day_outside: 'text-white/20',
  day_disabled: 'text-white/15 cursor-not-allowed',
  day_hidden: 'invisible',
};

const popoverClass =
  'absolute z-50 mt-2 p-3 rounded-2xl border border-white/10 bg-[#0B1020] shadow-2xl shadow-black/60 animate-in fade-in';
const fieldClass =
  'w-full flex items-center gap-2 bg-[#050914] border border-white/10 rounded-lg px-3 py-2.5 text-sm text-left text-white outline-none hover:border-white/20 focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400/50 transition-colors';

function useOutside(onClose: () => void) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('mousedown', handler);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('keydown', esc);
    };
  }, [onClose]);
  return ref;
}

/** Buka ke atas bila ruang di bawah tidak cukup */
function useFlipUp(open: boolean, ref: React.RefObject<HTMLDivElement>, needed = 430) {
  const [up, setUp] = useState(false);
  useEffect(() => {
    if (!open || !ref.current) return;
    const r = ref.current.getBoundingClientRect();
    setUp(window.innerHeight - r.bottom < needed && r.top > needed * 0.6);
  }, [open, ref, needed]);
  return up;
}

/** Value format: "yyyy-MM-dd" (date only) or "yyyy-MM-ddTHH:mm" (withTime) */
export function DateTimeField({
  value,
  onChange,
  withTime = false,
  placeholder = 'Pilih tanggal',
  testId,
  clearable = true,
}: {
  value: string;
  onChange: (v: string) => void;
  withTime?: boolean;
  placeholder?: string;
  testId?: string;
  clearable?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const ref = useOutside(() => setOpen(false));
  const up = useFlipUp(open, ref, withTime ? 420 : 360);

  const datePart = value ? value.slice(0, 10) : '';
  const timePart = withTime ? (value.slice(11, 16) || '00:00') : '';

  const selected = useMemo(() => {
    if (!datePart) return undefined;
    const d = parse(datePart, 'yyyy-MM-dd', new Date());
    return isValid(d) ? d : undefined;
  }, [datePart]);

  const commit = (date?: Date, time?: string) => {
    if (!date) return onChange('');
    const dStr = format(date, 'yyyy-MM-dd');
    onChange(withTime ? `${dStr}T${time || timePart || '00:00'}` : dStr);
  };

  const label = selected
    ? `${format(selected, 'd MMMM yyyy', { locale: localeId })}${withTime ? ` · ${timePart}` : ''}`
    : placeholder;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        data-testid={testId}
        onClick={() => setOpen((o) => !o)}
        className={fieldClass}
      >
        <CalendarIcon className="w-4 h-4 text-cyan-400/80 shrink-0" strokeWidth={1.5} />
        <span className={selected ? 'text-white' : 'text-white/35'}>{label}</span>
        {clearable && value && (
          <X
            data-testid={testId ? `${testId}-clear` : undefined}
            className="w-3.5 h-3.5 text-white/40 hover:text-white ml-auto shrink-0"
            strokeWidth={2}
            onClick={(e) => {
              e.stopPropagation();
              onChange('');
            }}
          />
        )}
      </button>

      {open && (
        <div className={`${popoverClass} ${up ? 'bottom-full mb-2 mt-0' : ''}`} data-testid={testId ? `${testId}-popover` : undefined}>
          <DayPicker
            mode="single"
            locale={localeId}
            weekStartsOn={1}
            defaultMonth={selected}
            selected={selected}
            onSelect={(d) => {
              commit(d || undefined);
              if (!withTime) setOpen(false);
            }}
            classNames={dayPickerClassNames}
          />
          {withTime && (
            <div className="flex items-center gap-2 border-t border-white/10 pt-3 mt-1">
              <span className="text-xs text-white/50">Jam</span>
              <input
                type="time"
                data-testid={testId ? `${testId}-time` : undefined}
                value={timePart}
                onChange={(e) => {
                  const t = e.target.value || '00:00';
                  commit(selected || new Date(), t);
                }}
                className="bg-[#050914] border border-white/10 rounded-lg px-2.5 py-1.5 text-sm text-white outline-none focus:border-cyan-400 [color-scheme:dark]"
              />
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="ml-auto text-xs px-3 py-1.5 rounded-lg bg-cyan-400 text-[#050914] font-medium hover:bg-cyan-300 transition-colors"
              >
                Selesai
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const PRESETS: { label: string; get: () => { from: Date; to: Date } }[] = [
  { label: 'Hari Ini', get: () => ({ from: startOfToday(), to: startOfToday() }) },
  { label: '7 Hari', get: () => ({ from: subDays(startOfToday(), 6), to: startOfToday() }) },
  { label: '30 Hari', get: () => ({ from: subDays(startOfToday(), 29), to: startOfToday() }) },
  { label: 'Bulan Ini', get: () => ({ from: startOfMonth(startOfToday()), to: startOfToday() }) },
];

/** from/to format: "yyyy-MM-dd" */
export function DateRangeField({
  from,
  to,
  onChange,
  testId,
  placeholder = 'Pilih rentang tanggal',
}: {
  from: string;
  to: string;
  onChange: (v: { from: string; to: string }) => void;
  testId?: string;
  placeholder?: string;
}) {
  const [open, setOpen] = useState(false);
  const ref = useOutside(() => setOpen(false));
  const up = useFlipUp(open, ref, 400);

  const toDate = (s: string) => {
    if (!s) return undefined;
    const d = parse(s, 'yyyy-MM-dd', new Date());
    return isValid(d) ? d : undefined;
  };
  const range: DateRange | undefined = from || to ? { from: toDate(from), to: toDate(to) } : undefined;

  const emit = (r?: DateRange) =>
    onChange({
      from: r?.from ? format(r.from, 'yyyy-MM-dd') : '',
      to: r?.to ? format(r.to, 'yyyy-MM-dd') : '',
    });

  const fmt = (s: string) => {
    const d = toDate(s);
    return d ? format(d, 'd MMM yyyy', { locale: localeId }) : '';
  };
  const label = from || to ? `${fmt(from) || '…'} — ${fmt(to) || '…'}` : placeholder;

  return (
    <div className="relative" ref={ref}>
      <button type="button" data-testid={testId} onClick={() => setOpen((o) => !o)} className={fieldClass}>
        <CalendarIcon className="w-4 h-4 text-cyan-400/80 shrink-0" strokeWidth={1.5} />
        <span className={from || to ? 'text-white' : 'text-white/35'}>{label}</span>
        {(from || to) && (
          <X
            data-testid={testId ? `${testId}-clear` : undefined}
            className="w-3.5 h-3.5 text-white/40 hover:text-white ml-auto shrink-0"
            strokeWidth={2}
            onClick={(e) => {
              e.stopPropagation();
              onChange({ from: '', to: '' });
            }}
          />
        )}
      </button>

      {open && (
        <div className={`${popoverClass} right-0 sm:right-auto ${up ? 'bottom-full mb-2 mt-0' : ''}`} data-testid={testId ? `${testId}-popover` : undefined}>
          <div className="flex flex-wrap gap-1.5 mb-3">
            {PRESETS.map((p) => (
              <button
                key={p.label}
                type="button"
                data-testid={testId ? `${testId}-preset-${p.label.toLowerCase().replace(/\s+/g, '-')}` : undefined}
                onClick={() => {
                  const r = p.get();
                  emit(r);
                  setOpen(false);
                }}
                className="text-[11px] px-2.5 py-1 rounded-full border border-white/10 text-white/70 hover:text-cyan-400 hover:border-cyan-400/40 transition-colors"
              >
                {p.label}
              </button>
            ))}
          </div>
          <DayPicker
            mode="range"
            locale={localeId}
            weekStartsOn={1}
            numberOfMonths={typeof window !== 'undefined' && window.innerWidth >= 768 ? 2 : 1}
            defaultMonth={range?.from}
            selected={range}
            onSelect={(r) => {
              emit(r || undefined);
              if (r?.from && r?.to) setOpen(false);
            }}
            classNames={dayPickerClassNames}
          />
        </div>
      )}
    </div>
  );
}
